<template>
  <div class="module-layout">
    <div class="parameter-panel">
      <h4>
        <i class="el-icon-setting"></i>
        Slice Browser
        <FilterButton style="margin-left: auto;" :project="project" @change="onFilterChanged" />
      </h4>

      <div class="parameter-container">
        <div class="parameter-group">
          <label>View:</label>
          <el-radio-group v-model="viewMode" size="small">
            <el-radio-button label="table">Table</el-radio-button>
            <el-radio-button label="folder">Folder</el-radio-button>
          </el-radio-group>
        </div>

        <div v-if="false" class="parameter-group" style="margin-top: 14px;">
          <label>Magnification:</label>
          <span style="color: #606266;">
            {{ currentMagnification || '-' }}
          </span>
        </div>

        <div v-if="viewMode === 'folder'" style="margin-top: 14px;">
          <div class="folder-path">
            <i class="el-icon-folder-opened"></i>
            <span style="margin-left: 6px;">{{ currentPathLabel }}</span>
          </div>

          <el-tree
            class="slice-tree"
            node-key="id"
            :data="treeData"
            :default-expanded-keys="defaultExpandedKeys"
            :expand-on-click-node="false"
            @node-click="onTreeNodeClick"
          >
            <span class="custom-tree-node" slot-scope="{ node, data }">
              <i
                v-if="data.type === 'folder'"
                :class="node.expanded ? 'el-icon-folder-opened' : 'el-icon-folder'"
                style="margin-right: 6px; color: #409EFF;"
              ></i>
              <i
                v-else
                class="el-icon-picture-outline"
                style="margin-right: 6px; color: #67C23A;"
              ></i>
              <span>{{ data.label }}</span>
            </span>
          </el-tree>
        </div>
      </div>
    </div>

    <div class="results-panel">
      <!-- Full Image View -->
      <div v-if="showImageView" class="full-image-view">
        <h4>
          <el-button
            type="text"
            icon="el-icon-back"
            @click="backToBrowser"
            style="margin-right: 16px;"
          >
            Back
          </el-button>
          <i class="el-icon-picture"></i>
          Full Resolution Image
        </h4>

        <div v-if="fullImageLoading" style="text-align: center; padding: 60px;">
          <i class="el-icon-loading" style="font-size: 40px; color: #409EFF;"></i>
          <p style="margin-top: 16px; color: #909399;">Loading full image...</p>
        </div>

        <div v-else style="flex: 1; overflow-y: auto;">
          <div style="margin-bottom: 16px; padding: 12px; background: #f5f7fa; border-radius: 4px;">
            <span><strong>Sample ID:</strong> {{ currentSampleID }}</span>
            <span v-if="currentResolutionLabel" style="margin-left: 24px;">
              <strong>Resolution:</strong> {{ currentResolutionLabel }}
            </span>
          </div>
          <div style="text-align: center; background: #000; border-radius: 8px; padding: 20px; overflow: auto;">
            <img
              v-if="fullImageBase64"
              :src="'data:image/jpeg;base64,' + fullImageBase64"
              style="max-width: 100%; max-height: 70vh; box-shadow: 0 4px 12px rgba(0,0,0,0.3);"
            />
          </div>
        </div>
      </div>

      <!-- Table View -->
      <div v-else-if="viewMode === 'table'" style="display: flex; flex-direction: column; height: 100%; overflow: hidden;">
        <h4>
          <i class="el-icon-picture"></i>
          Histopathology Images - Table View
          <span style="font-size: 12px; color: #909399; font-weight: normal; margin-left: 12px;">
            Click thumbnail to view full image
          </span>
        </h4>

        <div v-if="loading" style="text-align: center; padding: 60px;">
          <i class="el-icon-loading" style="font-size: 40px; color: #409EFF;"></i>
          <p style="margin-top: 16px; color: #909399;">Loading slice images...</p>
        </div>

        <div v-else-if="tableData.length === 0" style="text-align: center; padding: 60px;">
          <i class="el-icon-picture-outline" style="font-size: 60px; color: #C0C4CC;"></i>
          <p style="margin-top: 16px; color: #909399;">No slice images available</p>
          <p style="color: #C0C4CC; font-size: 12px;">Try adjusting filters</p>
        </div>

        <div v-else style="flex: 1; display: flex; flex-direction: column; overflow: hidden; min-height: 0;">
          <div style="flex: 1; overflow: hidden;">
            <el-table
              :data="paginatedData"
              class="slice-table"
              style="width: 100%;"
              height="100%"
              stripe
            >
              <el-table-column label="Image" width="120" fixed>
                <template slot-scope="scope">
                  <el-tooltip
                    placement="top"
                    :disabled="!resolutionTooltip(scope.row)"
                    :content="resolutionTooltip(scope.row)"
                  >
                    <img
                      v-if="scope.row.thumbnail_base64"
                      :src="'data:image/jpeg;base64,' + scope.row.thumbnail_base64"
                      @mouseenter="prefetchImageInfo(scope.row)"
                      @click="openFullImage(scope.row.sampleID)"
                      style="width: 80px; height: 80px; object-fit: cover; cursor: pointer; border-radius: 4px; border: 2px solid #e2e8f0;"
                      :title="'Click to view full image'"
                    />
                  </el-tooltip>
                  <div v-if="!scope.row.thumbnail_base64" style="width: 80px; height: 80px; background: #f5f5f5; display: flex; align-items: center; justify-content: center;">
                    <i class="el-icon-picture-outline" style="font-size: 24px; color: #ccc;"></i>
                  </div>
                </template>
              </el-table-column>

              <el-table-column prop="patientID" label="Patient ID" width="160" fixed />

              <el-table-column
                v-for="key in metaColumns"
                :key="'meta_' + key"
                :label="formatVariableName(key)"
                :min-width="150"
              >
                <template slot-scope="scope">
                  {{ (scope.row.meta_variables && scope.row.meta_variables[key]) || '-' }}
                </template>
              </el-table-column>

              <el-table-column
                v-for="key in numericalColumns"
                :key="'numerical_' + key"
                :label="formatVariableName(key)"
                :min-width="150"
              >
                <template slot-scope="scope">
                  {{ (scope.row.numerical_variables && scope.row.numerical_variables[key]) != null ? Number(scope.row.numerical_variables[key]).toFixed(3) : '-' }}
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div style="padding: 16px; border-top: 1px solid #dcdfe6; display: flex; justify-content: space-between; align-items: center; background: #f5f7fa;">
            <span style="font-size: 14px; color: #606266;">
              Total: {{ tableData.length }} images
            </span>
            <el-pagination
              @size-change="(val) => { pageSize = val; currentPage = 1; }"
              @current-change="(val) => currentPage = val"
              :current-page="currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pageSize"
              :total="tableData.length"
              layout="sizes, prev, pager, next"
            />
          </div>
        </div>
      </div>

      <!-- Folder View -->
      <div v-else class="folder-view">
        <h4>
          <i class="el-icon-folder-opened"></i>
          Histopathology Images - Folder View
          <span style="font-size: 12px; color: #909399; font-weight: normal; margin-left: 12px;">
            Hover a thumbnail to see resolution
          </span>
          <el-input
            v-model="folderSearchQuery"
            size="small"
            clearable
            prefix-icon="el-icon-search"
            placeholder="Search sample ID / filename"
            style="margin-left: auto; max-width: 280px;"
          />
        </h4>

        <div v-if="loading" style="text-align: center; padding: 60px;">
          <i class="el-icon-loading" style="font-size: 40px; color: #409EFF;"></i>
          <p style="margin-top: 16px; color: #909399;">Loading slice images...</p>
        </div>

        <div v-else-if="tableData.length === 0" style="text-align: center; padding: 60px;">
          <i class="el-icon-folder" style="font-size: 60px; color: #C0C4CC;"></i>
          <p style="margin-top: 16px; color: #909399;">No slice images available</p>
        </div>

        <div v-else-if="filteredFolderRows.length === 0" style="text-align: center; padding: 60px;">
          <i class="el-icon-search" style="font-size: 50px; color: #C0C4CC;"></i>
          <p style="margin-top: 16px; color: #909399;">No matches</p>
          <p style="color: #C0C4CC; font-size: 12px;">Try a different ID keyword</p>
        </div>

        <div v-else class="thumb-grid">
          <div
            v-for="row in filteredFolderRows"
            :key="row.sampleID"
            class="thumb-card"
            @click="openFullImage(row.sampleID)"
            @mouseenter="prefetchImageInfo(row)"
          >
            <el-tooltip placement="top" :content="hoverTooltipContent(row)" :disabled="!hoverTooltipContent(row)">
              <div class="thumb-inner">
                <img
                  v-if="row.thumbnail_base64"
                  :src="'data:image/jpeg;base64,' + row.thumbnail_base64"
                  class="thumb-img"
                />
                <div v-else class="thumb-img thumb-placeholder">
                  <i class="el-icon-picture-outline" style="font-size: 30px; color: #c0c4cc;"></i>
                </div>
                <div class="thumb-label">
                  {{ row.filename || row.sampleID }}
                </div>
              </div>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import FilterButton from '@/components/Widgets/DatasetFilterButton.vue'

export default {
  name: 'HistopathologySliceBrowser',
  components: { FilterButton },
  props: {
    datasetId: { type: [String, Number], required: true },
    project: { type: Object, required: true },
    magnifications: { type: Array, default: () => [] },
    obsFilter: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      viewMode: 'table',
      currentMagnification: '',
      folderSearchQuery: '',

      loading: false,
      tableData: [],
      metaColumns: [],
      numericalColumns: [],
      currentPage: 1,
      pageSize: 20,

      showImageView: false,
      currentSampleID: null,
      fullImageBase64: null,
      fullImageLoading: false,

      imageInfoCache: {}, // key: `${mag}::${sampleID}` -> { width, height }
      imageInfoPending: {} // key -> true
    }
  },
  computed: {
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return (Array.isArray(this.tableData) ? this.tableData : []).slice(start, end)
    },
    defaultExpandedKeys() {
      const keys = ['slice-root']
      if (this.currentMagnification) keys.push(`mag:${this.currentMagnification}`)
      if (this.currentMagnification) keys.push(`raw:${this.currentMagnification}`)
      return keys
    },
    currentPathLabel() {
      if (!this.currentMagnification) return 'slice'
      return `slice / ${this.currentMagnification} / raw`
    },
    treeData() {
      const mags = Array.isArray(this.magnifications) ? this.magnifications : []
      const root = {
        id: 'slice-root',
        type: 'folder',
        label: 'slice',
        children: mags.map(mag => {
          const isCurrent = mag === this.currentMagnification
          const files = isCurrent ? this.filteredFolderRows : []
          return {
            id: `mag:${mag}`,
            type: 'folder',
            label: mag,
            mag,
            children: [
              {
                id: `raw:${mag}`,
                type: 'folder',
                label: 'raw',
                mag,
                children: files.map(row => ({
                  id: `file:${mag}:${row.sampleID}`,
                  type: 'file',
                  label: row.filename || row.sampleID,
                  mag,
                  sampleID: row.sampleID
                }))
              }
            ]
          }
        })
      }
      return [root]
    },
    filteredFolderRows() {
      const rows = Array.isArray(this.tableData) ? this.tableData : []
      const q = String(this.folderSearchQuery || '').trim().toLowerCase()
      if (!q) return rows
      return rows.filter(r => {
        const sid = String((r && r.sampleID) || '').toLowerCase()
        const fn = String((r && r.filename) || '').toLowerCase()
        return sid.includes(q) || fn.includes(q)
      })
    },
    currentResolutionLabel() {
      if (!this.currentSampleID || !this.currentMagnification) return ''
      const key = `${this.currentMagnification}::${this.currentSampleID}`
      const info = this.imageInfoCache[key]
      return info && info.width && info.height ? `${info.width}×${info.height}` : ''
    }
  },
  watch: {
    magnifications: {
      immediate: true,
      handler() {
        this.ensureMagnificationAndLoad()
      }
    },
    obsFilter: {
      deep: true,
      handler() {
        // Auto-refresh when filters change (no "Load Images" button anymore)
        this.ensureMagnificationAndLoad()
      }
    }
  },
  methods: {
    onFilterChanged(f) {
      this.$emit('obs-filter-change', f)
    },
    formatVariableName(name) {
      const s = String(name || '')
      // Strip common prefixes
      const cleaned = s.replace(/^meta_/, '').replace(/^numerical_/, '')
      return cleaned.split('_').map(w => (w ? (w.charAt(0).toUpperCase() + w.slice(1)) : '')).join(' ')
    },
    ensureMagnificationAndLoad() {
      const mags = Array.isArray(this.magnifications) ? this.magnifications : []
      if (mags.length === 0) {
        this.currentMagnification = ''
        this.tableData = []
        this.metaColumns = []
        this.numericalColumns = []
        return
      }
      if (!this.currentMagnification || !mags.includes(this.currentMagnification)) {
        this.currentMagnification = mags[0]
      }
      this.loadSliceTableData()
    },
    async loadSliceTableData() {
      if (!this.currentMagnification) return

      this.loading = true
      this.tableData = []
      this.metaColumns = []
      this.numericalColumns = []
      this.currentPage = 1
      this.folderSearchQuery = ''

      try {
        const response = await axios.post('/api/projectDetail/get_slice_table_data', {
          datasetid: this.datasetId,
          magnification: this.currentMagnification,
          obs_filter: this.obsFilter
        })

        if (response.data && response.data.code === 200) {
          const rows = Array.isArray(response.data.data) ? response.data.data : []
          this.tableData = rows
          if (rows.length > 0) {
            this.metaColumns = Object.keys(rows[0].meta_variables || {})
            this.numericalColumns = Object.keys(rows[0].numerical_variables || {})
          }
        } else {
          this.$notify({
            title: 'Error',
            message: (response.data && response.data.error) || 'Failed to load slice images',
            type: 'error',
            position: 'top-right'
          })
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to load slice images: ' + (error && error.message ? error.message : String(error)),
          type: 'error',
          position: 'top-right'
        })
      } finally {
        this.loading = false
      }
    },
    onTreeNodeClick(data) {
      if (!data) return
      if (data.type === 'folder' && data.mag) {
        // Clicking a magnification/raw folder switches active magnification
        if (data.mag !== this.currentMagnification) {
          this.currentMagnification = data.mag
          this.loadSliceTableData()
        }
        return
      }
      if (data.type === 'file' && data.sampleID) {
        if (data.mag && data.mag !== this.currentMagnification) {
          this.currentMagnification = data.mag
        }
        this.openFullImage(data.sampleID)
      }
    },
    hoverTooltipContent(row) {
      const label = this.getResolutionLabel(row)
      if (label) return `Resolution: ${label}`
      if (this.isResolutionPending(row)) return 'Loading resolution...'
      return ''
    },
    resolutionTooltip(row) {
      const label = this.getResolutionLabel(row)
      if (label) return `Resolution: ${label}`
      if (this.isResolutionPending(row)) return 'Loading resolution...'
      return ''
    },
    getResolutionLabel(row) {
      if (!row || !row.sampleID || !this.currentMagnification) return ''
      const key = `${this.currentMagnification}::${row.sampleID}`
      const info = this.imageInfoCache[key]
      return info && info.width && info.height ? `${info.width}×${info.height}` : ''
    },
    isResolutionPending(row) {
      if (!row || !row.sampleID || !this.currentMagnification) return false
      const key = `${this.currentMagnification}::${row.sampleID}`
      return !!this.imageInfoPending[key]
    },
    async prefetchImageInfo(row) {
      if (!row || !row.sampleID || !this.currentMagnification) return
      const key = `${this.currentMagnification}::${row.sampleID}`
      if (this.imageInfoCache[key]) return
      if (this.imageInfoPending[key]) return

      this.$set(this.imageInfoPending, key, true)
      try {
        const resp = await axios.post('/api/projectDetail/get_slice_image_info', {
          datasetid: this.datasetId,
          magnification: this.currentMagnification,
          sampleID: row.sampleID
        })
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          const { width, height } = resp.data.data
          this.$set(this.imageInfoCache, key, { width, height })
        }
      } catch (e) {
        // Silent: hover should not spam notifications
      } finally {
        this.$delete(this.imageInfoPending, key)
      }
    },
    async openFullImage(sampleID) {
      if (!sampleID || !this.currentMagnification) return
      this.showImageView = true
      this.currentSampleID = sampleID
      this.fullImageBase64 = null
      this.fullImageLoading = true

      // Prefetch resolution info for header
      this.prefetchImageInfo({ sampleID })

      try {
        const response = await axios.post('/api/projectDetail/get_slice_full_image', {
          datasetid: this.datasetId,
          magnification: this.currentMagnification,
          sampleID
        })
        if (response.data && response.data.code === 200) {
          this.fullImageBase64 = response.data.data.image_base64
        } else {
          this.$notify({
            title: 'Error',
            message: (response.data && response.data.error) || 'Failed to load full image',
            type: 'error',
            position: 'top-right'
          })
          this.backToBrowser()
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to load full image: ' + (error && error.message ? error.message : String(error)),
          type: 'error',
          position: 'top-right'
        })
        this.backToBrowser()
      } finally {
        this.fullImageLoading = false
      }
    },
    backToBrowser() {
      this.showImageView = false
      this.currentSampleID = null
      this.fullImageBase64 = null
    }
  }
}
</script>

<style scoped>
.module-layout {
  display: flex;
  flex-direction: row;
  height: calc(100vh - 120px);
  gap: 16px;
  overflow: hidden;
}

.parameter-panel {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  flex: 0 0 350px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.parameter-panel h4 {
  background: #f8f9fa;
  margin: 0;
  padding: 20px;
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.parameter-panel h4 i {
  color: #3b82f6;
  font-size: 1.1rem;
}

.parameter-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 15px;
  flex: 1;
  overflow-y: auto;
}

.parameter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.parameter-group label {
  font-size: 12px;
  color: #606266;
  font-weight: 600;
}

.results-panel {
  background: white;
  border-radius: 12px;
  border:  1px solid #e9ecef;
  flex: 1;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.results-panel h4 {
  background: #f8f9fa;
  margin: 0;
  padding: 20px 24px;
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.results-panel h4 i {
  color: #3b82f6;
  font-size: 1.1rem;
}

.slice-tree {
  margin-top: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 8px;
  background: #fff;
  max-height: 55vh;
  overflow: auto;
}

.folder-path {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: #f5f7fa;
  border:  1px solid #e4e7ed;
  border-radius: 6px;
  color: #606266;
  font-size: 13px;
}

.thumb-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  padding: 12px;
  overflow: auto;
}

.thumb-card {
  cursor: pointer;
}

.thumb-inner {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.08s ease, box-shadow 0.08s ease;
}

.thumb-inner:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
}

.thumb-img {
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
  background: #f5f7fa;
}

.thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumb-label {
  padding: 8px 10px;
  font-size: 12px;
  color: #606266;
  border-top: 1px solid #f0f2f5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>


